import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { FormFieldsData, useFormContext } from 'framework/form';
import { ShowData, ShowContextProvider, useShowContext } from 'framework/show';
import { ShowDate } from 'framework/ui/ui-builder';
import { UiThemeOverrider } from 'framework/uiTheme';
import { TextField } from 'framework/ui/field';
import { EMPTY_VALUE_INDICATOR, DATE_FORMAT } from 'framework/utils';
import EntityHeaderField from './EntityHeaderField';
import type { Data } from 'framework/types';

export enum HEADER_SIZES {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
}

interface EntityHeaderLayoutProps {
  value: string;
  label: string;
  className?: string;
}

export const EntityHeaderMetaFieldLayout = ({ value, label, className = '' }: EntityHeaderLayoutProps) => {
  return value && value !== EMPTY_VALUE_INDICATOR ? (
    <div className={className}>
      <i>
        {label} {value}
      </i>
    </div>
  ) : null;
};

const EntityHeaderFieldLayout = ({ value, label, className = '' }: EntityHeaderLayoutProps) => {
  return value && value !== EMPTY_VALUE_INDICATOR ? (
    <div className={className}>
      {label} {value}
    </div>
  ) : null;
};

interface EntityHeaderProps {
  entityTitle?: React.ReactNode | ((data: Data | FormFieldsData) => React.ReactNode);
  entityTitleSource?: string;
  children?: React.ReactElement | React.ReactElement[];
  badges?: React.ReactNode | ((data: Data | FormFieldsData) => React.ReactNode);
  hasAuthor?: boolean;
  size?: HEADER_SIZES;
  defaultAuthor?: string;
  color?: TypographyProps['color'];
}

const EntityHeader = ({
  entityTitle,
  entityTitleSource,
  children,
  badges = null,
  hasAuthor = true,
  size = HEADER_SIZES.LARGE,
  defaultAuthor = '',
  color,
}: EntityHeaderProps) => {
  const formContext = useFormContext();
  const showContext = useShowContext();

  const data = (showContext?.data.isNotEmpty() && showContext.data) || new ShowData(formContext?.data || {});

  const createdAt = data.getValueBySource('createdAt');
  const updatedAt = data.getValueBySource('updatedAt');
  const createdByName = data.getValueBySource('createdByName');
  const updatedByName = data.getValueBySource('updatedByName');

  const displayedName = useMemo(() => {
    if (typeof entityTitle === 'function') {
      return entityTitle(data);
    } else if (entityTitle) {
      return entityTitle;
    } else if (entityTitleSource) {
      return data.getValueBySource(entityTitleSource);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityTitle, data]);

  const displayedBadges = typeof badges === 'function' ? badges(data) : badges;

  const EntityName = useMemo(() => {
    let EntityNameTag: keyof JSX.IntrinsicElements;

    if (size === HEADER_SIZES.LARGE) {
      EntityNameTag = 'h1';
    } else if (size === HEADER_SIZES.MEDIUM) {
      EntityNameTag = 'h2';
    } else {
      EntityNameTag = 'h3';
    }

    return (
      <Typography variant={EntityNameTag} color={color} className="entity-header__name">
        {displayedName}
        <Typography component="span" variant="inherit" className="entity-header__badges">
          {displayedBadges}
        </Typography>
      </Typography>
    );
  }, [displayedBadges, displayedName, size, color]);

  return (
    <ShowContextProvider
      value={{
        data,
      }}
    >
      <UiThemeOverrider overriddenLayouts={{ BaseFieldLayout: EntityHeaderMetaFieldLayout }}>
        <Box>{EntityName}</Box>
        <Box
          sx={theme => ({
            color: 'text.secondary',
            fontSize: '10px',
            margin: 0,
            padding: 0,
            [theme.breakpoints.down('smallMobile')]: { display: 'none' },
            [theme.breakpoints.between('sm', 'lg')]: {
              padding: 0,
              order: -1,
              marginTop: '-10px',
            },
            [theme.breakpoints.up('lg')]: {
              position: 'absolute',
              top: '20px',
              right: '25px',
              textAlign: 'right',
              margin: 0,
            },
          })}
        >
          <TextField
            label="Created"
            value={
              createdAt && (
                <>
                  <ShowDate variant="caption" dateUTC={createdAt} format={DATE_FORMAT.FULL} />
                  {hasAuthor && ` by ${createdByName || defaultAuthor}`}
                </>
              )
            }
          />
          <TextField
            label="Last modified"
            value={
              updatedAt && (
                <>
                  <ShowDate variant="caption" dateUTC={updatedAt} format={DATE_FORMAT.FULL} />
                  {hasAuthor && ` by ${updatedByName || defaultAuthor}`}
                </>
              )
            }
          />
        </Box>
        {React.Children.map(children, child => {
          if (!child) return null;
          const { metaInformation } = child.props;

          if (!metaInformation) {
            return (
              <UiThemeOverrider
                overriddenLayouts={{
                  BaseFieldLayout: EntityHeaderFieldLayout,
                }}
              >
                <EntityHeaderField>{child}</EntityHeaderField>
              </UiThemeOverrider>
            );
          } else {
            return child;
          }
        })}
      </UiThemeOverrider>
    </ShowContextProvider>
  );
};

export default EntityHeader;
